import { ButtonGroup, FormControl, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMount, useSessionStorage } from "react-use";

import { Button } from "adminComponents/atoms/Button";
import { FormLabel } from "adminComponents/atoms/FormLabel";
import { Modal } from "adminComponents/atoms/Modal";
import { pxToRem } from "adminComponents/utils/pxToRem";
import { afterSignInPathSessionKey } from "lib/constants";
import { hairColorHexCodes, skinToneHexCodes } from "links/lib/constants";
import { useFetchAvatarHairStyles } from "links/lib/features/avatars";
import { IAvatarItem } from "links/lib/types";
import { HairStylePicker } from "sharedComponents/molecules/HairStylePicker";
import { QuickColorPicker } from "sharedComponents/molecules/QuickColorPicker";

export interface ISignInModalProps {
  signInLinkTo: string;
  isGuestCreatePending: boolean;
  onGuestCreate?: (opts: {
    skinTone: string;
    hairColor: string;
    hairStyleAvatarItemId: string;
  }) => void;
  isOpen: boolean;
}

export interface ISignInModalBodyProps {
  onContinueAsGuest: () => void;
  signInLinkTo: string;
}

export interface IGuestModalBodyProps {
  onCancel: () => void;
  selectedHairColor: string;
  selectedSkinTone: string;
  selectedHairStyle?: IAvatarItem;
  hairStyleOptions: Array<IAvatarItem>;
  onHairColorSelect: (hairColor: string) => void;
  onSkinToneSelect: (skinTone: string) => void;
  onHairStyleSelect: (hairStyle: IAvatarItem) => void;
  isGuestCreatePending: boolean;
  onGuestCreate?: () => void;
}

const SignInModalBody: React.FC<ISignInModalBodyProps> = ({
  onContinueAsGuest,
  signInLinkTo,
}) => {
  const { t } = useTranslation("session", {
    keyPrefix: "signInModal.signIn",
    useSuspense: false,
  });

  const [, setSessionRedirectPath] = useSessionStorage(
    afterSignInPathSessionKey
  );

  useMount(() => {
    setSessionRedirectPath(location.pathname);
  });

  return (
    <VStack spacing={pxToRem(24)} alignItems="left">
      <Text variant="adminP1">{t("instructionText")}</Text>
      <ButtonGroup>
        <Button variant="adminButtonFilled" as={Link} to={signInLinkTo}>
          {t("signInButton")}
        </Button>
        <Button onClick={onContinueAsGuest} variant="adminButtonOutlined">
          {t("guestModeButton")}
        </Button>
      </ButtonGroup>
    </VStack>
  );
};

const GuestModalBody: React.FC<IGuestModalBodyProps> = ({
  onCancel,
  selectedHairColor,
  selectedSkinTone,
  selectedHairStyle,
  hairStyleOptions,
  onHairColorSelect,
  onSkinToneSelect,
  onHairStyleSelect,
  isGuestCreatePending,
  onGuestCreate,
}) => {
  const { t: tCommon } = useTranslation("admin", {
    keyPrefix: "common",
    useSuspense: false,
  });
  const { t } = useTranslation("session", {
    keyPrefix: "signInModal.guestMode",
    useSuspense: false,
  });

  return (
    <VStack spacing={pxToRem(24)} alignItems="left">
      <Text variant="adminP1">{t("instructionText")}</Text>
      <FormControl variant="adminFormControl">
        <FormLabel>
          {t("skinToneLabel")}
          {tCommon("requiredAsterisk")}
        </FormLabel>
        <QuickColorPicker
          colorOptions={skinToneHexCodes}
          onColorSelect={onSkinToneSelect}
          selectedColor={selectedSkinTone}
        />
      </FormControl>
      <FormControl variant="adminFormControl">
        <FormLabel>
          {t("hairColorLabel")}
          {tCommon("requiredAsterisk")}
        </FormLabel>
        <QuickColorPicker
          colorOptions={hairColorHexCodes}
          onColorSelect={onHairColorSelect}
          selectedColor={selectedHairColor}
        />
      </FormControl>
      <FormControl>
        <FormLabel>
          {t("hairStyleLabel")}
          {tCommon("requiredAsterisk")}
        </FormLabel>
        <HairStylePicker
          hairColor={selectedHairColor || undefined}
          skinToneColor={selectedSkinTone || undefined}
          hairStyleOptions={hairStyleOptions}
          onHairStyleSelect={onHairStyleSelect}
          selectedHairStyle={selectedHairStyle}
        />
      </FormControl>
      <ButtonGroup pt={pxToRem(8)}>
        <Button
          onClick={onCancel}
          variant="adminButtonOutlined"
          disabled={isGuestCreatePending}
        >
          {t("cancelButton")}
        </Button>
        <Button
          isDisabled={
            !selectedHairColor || !selectedSkinTone || !selectedHairStyle
          }
          onClick={onGuestCreate}
          variant="adminButtonFilled"
          isLoading={isGuestCreatePending}
        >
          {t("submitButton")}
        </Button>
      </ButtonGroup>
    </VStack>
  );
};

const SignInModal: React.FC<ISignInModalProps> = ({
  signInLinkTo,
  isGuestCreatePending,
  onGuestCreate,
  isOpen,
}) => {
  const [continueAsGuest, setContinueAsGuest] = useState(false);
  const [hairColor, setHairColor] = useState("");
  const [skinTone, setSkinTone] = useState("");
  const [selectedHairStyle, setSelectedHairStyle] = useState<IAvatarItem>();
  const { t } = useTranslation("session", {
    keyPrefix: "signInModal",
    useSuspense: false,
  });

  const fetchAvatarHairStyles = useFetchAvatarHairStyles({});

  const modalTitle = !continueAsGuest
    ? t("signIn.modalTitle")
    : t("guestMode.modalTitle");

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      hideClose={true}
      showBackButton={false}
      onClose={() => {
        // No-op - should not allow close
      }}
    >
      {continueAsGuest ? (
        <GuestModalBody
          selectedHairColor={hairColor}
          selectedSkinTone={skinTone}
          selectedHairStyle={selectedHairStyle}
          hairStyleOptions={fetchAvatarHairStyles.data?.items || []} // TODO
          onSkinToneSelect={setSkinTone}
          onHairColorSelect={setHairColor}
          onHairStyleSelect={setSelectedHairStyle}
          onCancel={() => setContinueAsGuest(false)}
          isGuestCreatePending={isGuestCreatePending}
          onGuestCreate={() => {
            onGuestCreate?.({
              skinTone,
              hairColor,
              hairStyleAvatarItemId: selectedHairStyle?.id || "0",
            });
          }}
        />
      ) : (
        <SignInModalBody
          signInLinkTo={signInLinkTo}
          onContinueAsGuest={() => setContinueAsGuest(true)}
        />
      )}
    </Modal>
  );
};

export { SignInModal };
